import { KeyValuePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { LookupService } from "@app/cem/project/services/lookup.service";
import { StdCfgSharedServiceService } from "@app/cem/project/standard-config/std-cfg-shared-service.service";
import { PlexEnvironment } from '@app/env';
import { PxdAgGridCheckboxRenderer, PxdAgGridCheckboxRendererReadOnly } from "@app/shared/aggrid/pxd-ag-grid-cell-renderers";
import { PxdAgDatePickerEditorComponent } from '@app/shared/aggrid/pxd-ag-grid-datepicker-editor';
import { PxdAgEditGridDefaultColumnDef, PxdAgGridColumnTypes, PxdAgGridExcelStyles, PxdAgGridOptions, PxdAgGridSideBarOptions, PxdAgGridStatusBarOptions, PXDDefaultExcelExportParams } from "@app/shared/aggrid/pxd-ag-grid-options";
import { PxdAgGridSelectEditorComponent } from "@app/shared/aggrid/pxd-ag-grid-select-editor";
import { FunctionIds } from '@app/shared/data/project-enums';
import { IConstructionTypeDto } from '@app/shared/models/dtos/construction-type';
import { IFunctionDto } from '@app/shared/models/dtos/function';
import { IProjectSubTypeDto } from "@app/shared/models/dtos/project-subtype";
import { IProjectTypeDto } from "@app/shared/models/dtos/project-type";
import { IStandardConfigDto } from "@app/shared/models/dtos/standard-config";
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { DialogActionsComponent, DialogComponent } from '@progress/kendo-angular-dialog';
import { AgGridAngular } from "ag-grid-angular";
import { CellEditingStartedEvent, CellValueChangedEvent, GridApi, GridReadyEvent, IRowNode, SelectionChangedEvent } from "ag-grid-community";
import moment from "moment";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: 'app-stg-cfg-details',
  templateUrl: './stg-cfg-details.component.html',
  styleUrls: ['./stg-cfg-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    KeyValuePipe,
    AgGridAngular,
    DialogComponent,
    DialogActionsComponent,
    ButtonDirective,
  ],
})
export class StgCfgDetailsComponent implements OnInit {
  public stdCfgSharedServiceService = inject(StdCfgSharedServiceService);
  private lookupService = inject(LookupService);
  private environment = inject(PlexEnvironment);

  addNewStandardConfigDto(selectedFunction: IFunctionDto) {
    let newItem = this.stdCfgSharedServiceService.addNewStandardConfigDto(this.selectedFunction);
    this.setRowFromItem(newItem);
  }

  private setRowFromItem(item: IStandardConfigDto): void {
    if (!this.gridApi) return;
    let lastMaxNode = 0;
    let lastNode = null;
    // focuses grid onto new standard config and selects the new standard config
    this.gridApi.forEachNode(node => {
      if (node.rowIndex > lastMaxNode) {
        if (lastNode) return;
        if (node.data == item) {
          node.setSelected(true, true);
          this.gridApi.ensureIndexVisible(node.rowIndex, null);
          lastNode = node;
        }
      }
    });
  }

  public iStandardConfigDtosAll: Array<IStandardConfigDto> = new Array<IStandardConfigDto>();
  public iStandardConfigDtos: Array<IStandardConfigDto> = new Array<IStandardConfigDto>();
  public IProjectTypeDtos: Array<IProjectTypeDto> = new Array<IProjectTypeDto>();
  public IProjectSubTypeDtos: Array<IProjectSubTypeDto> = new Array<IProjectSubTypeDto>();
  public IConstructionTypeDtos: Array<IConstructionTypeDto> = new Array<IConstructionTypeDto>();
  private selectedFunction: IFunctionDto;
  public editedRow: IRowNode = null;
  private pendingIStandardConfigDto: IStandardConfigDto;

  private _selectedIStandardConfigDto: IStandardConfigDto = null;
  public set selectedIStandardConfigDto(value: IStandardConfigDto) {
    this._selectedIStandardConfigDto = value;
  }
  public get selectedIStandardConfigDto(): IStandardConfigDto {
    return this._selectedIStandardConfigDto;
  }
  private unsubscribe$: Subject<boolean> = new Subject();
  private unsubscribeStdConfig$: Subject<boolean> = new Subject();
  private gridApi: GridApi;
  public dialogOpened = false;
  private projectTypeEditable: boolean = true;
  public showValidationErrors = true;
  private confirmDoNotChangeStdConfig: boolean = false;

  async ngOnInit() {
    await this.initSelectedFunction();
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
    this.unsubscribe$.complete();
    this.unsubscribeStdConfig$.next(true);
    this.unsubscribeStdConfig$.complete();
  }

  public gridOptions: PxdAgGridOptions = {
    enableCharts: true,
    cellSelection: true,
    rowSelection: { mode: 'singleRow', checkboxes: false, enableClickSelection: true },
    rowGroupPanelShow: 'never',
    defaultColDef: PxdAgEditGridDefaultColumnDef,
    statusBar: PxdAgGridStatusBarOptions,
    sideBar: PxdAgGridSideBarOptions,
    columnTypes: PxdAgGridColumnTypes,
    excelStyles: PxdAgGridExcelStyles,
    defaultExcelExportParams: PXDDefaultExcelExportParams,

    /************************************
     * This event sets the filter state
     * of the ag-grid based on the users
     * saved filter state.
     ************************************/
    onFirstDataRendered: (params) => {
      try {
        let filterState = localStorage.getItem('stg-cfg-details:filterState');

        if (filterState == null)
          filterState = "{\"IsActive\":{\"values\":[\"true\"],\"filterType\":\"set\"}}";

        params.api.setFilterModel(JSON.parse(filterState));
      }
      catch (e) {
        console.warn('Error setting filter state:', e);
      }
    },

    /****************************************
     * Fires when the filtered column changes
     * or its value changes to save the
     * current state of the filtered state.
    *****************************************/
    onFilterChanged(params): void {
      try {
        let filterState = JSON.stringify(params.api.getFilterModel());
        localStorage.setItem('stg-cfg-details:filterState', filterState);
      }
      catch (e) {
        console.warn('Error on filter changed:', e);
      }
    },

    columnDefs: [
      {
        field: 'StandardConfigurationId', headerName: 'Id', editable: false, width: 60
      },
      {
        field: 'Name',
        headerName: 'Description',
        width: 275
      },
      {
        field: 'ProjectTypeId', headerName: 'Project Type', editable: true,
        valueGetter: params => {
          return this.getIProjectTypeDtosById(params.data.ProjectTypeId);
        },
        valueSetter: params => {
          params.data.ProjectTypeId = params.newValue;
          params.data.ProjectSubTypeId = null;
          return true;
        },
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              value: params.data.ProjectTypeId
              , readonly: !this.projectTypeEditable
              , values: this.activeProjectTypes(params)
            }
          };
        },
        width: 140
      },
      {
        field: 'ProjectSubTypeId', headerName: 'Project Sub Type', editable: true,
        valueGetter: params => {
          return this.getIProjectSubTypeDtosById(params.data.ProjectSubTypeId);
        },
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              values: this.IProjectSubTypeDtos
                .filter(p => p.ProjectTypeId === params.data.ProjectTypeId)
                .map(d => { return { value: d.ProjectSubTypeId, text: d.Description } })
              , value: params.data.ProjectSubTypeId
            }

          }
        }

        , width: 140
      },
      {
        field: 'ConstructionTypeId',
        headerName: 'Construction Type',
        editable: true,
        valueGetter: params => {
          return this.getIConstructionTypeDtosById(params.data.ConstructionTypeId);
        },
        cellEditorSelector: params => {
          return {
            component: PxdAgGridSelectEditorComponent,
            params: {
              values: this.IConstructionTypeDtos.map(d => { return { value: d.ConstructionTypeId, text: d.Description } }),
              value: params.data.ConstructionTypeId
            }
          }
        }
        ,
        width: 140
      },
      {
        field: 'EffectiveDate',
        headerName: 'Effective Date',
        type: ['dateColumn'],
        cellClass: 'ExcelDate',
        editable: true,
        cellEditorSelector: params => {
          return {
            component: PxdAgDatePickerEditorComponent,
            params: {
              minDate: moment("01/01/2000").toDate(),
              value: params.data.EffectiveDate
            }
          }
        },
        width: 120
      },
      {
        field: 'IsDefault',
        editable: true,
        singleClickEdit: true,
        headerName: 'PAT-Midland Default',
        cellRenderer: PxdAgGridCheckboxRendererReadOnly,
        cellEditorSelector: params => {
          return { component: PxdAgGridCheckboxRenderer };
        },
        width: 160,
      },
      {
        field: 'IsDELDefault',
        editable: true,
        singleClickEdit: true,
        headerName: 'DEL Default',
        cellRenderer: PxdAgGridCheckboxRendererReadOnly,
        cellEditorSelector: params => {
          return { component: PxdAgGridCheckboxRenderer };
        },
        width: 110,
        hide: !this.environment.showDelawareBasin,
        suppressColumnsToolPanel: !this.environment.showDelawareBasin,
        suppressFiltersToolPanel: !this.environment.showDelawareBasin
      },
      {
        field: 'IsActive',
        editable: true,
        singleClickEdit: true,
        headerName: 'Active',
        cellRenderer: PxdAgGridCheckboxRendererReadOnly,
        cellEditorSelector: params => {
          return { component: PxdAgGridCheckboxRenderer };
        },
        width: 80
      }
    ]
  };

  private activeProjectTypes(params: any) {
    let projectTypesFiltered = [];
    this.IProjectTypeDtos.forEach(p => {
      if (p.FunctionIds && p.FunctionIds.includes(params.data.FunctionId as number)) {
        projectTypesFiltered.push({ value: p.ProjectTypeId, text: p.Description })
      }
    });
    return projectTypesFiltered;
  }

  async initSelectedFunction() {
    this.stdCfgSharedServiceService.getSelectedFunctionDto().pipe(takeUntil(this.unsubscribe$)).subscribe(async response => {
      this.selectedFunction = response;
      await this.loadGrid();
    }
    );

    let projectTypeData = await this.lookupService.findProjectTypeDtos();
    this.IProjectTypeDtos = projectTypeData.Results;

    let projectSubTypeData = await this.lookupService.findProjectActiveSubTypeDtos();
    this.IProjectSubTypeDtos = projectSubTypeData.Results;

    let constructionTypeData = await this.lookupService.findConstructionTypeDtos();
    this.IConstructionTypeDtos = constructionTypeData.Results;

    this.stdCfgSharedServiceService.getStandardConfigDtos().pipe(takeUntil(this.unsubscribeStdConfig$)).subscribe(async response => {
      this.iStandardConfigDtosAll = response;
      await this.loadGrid();
    }
    );

    this.stdCfgSharedServiceService.getSelectedStandardConfigDto().pipe(takeUntil(this.unsubscribeStdConfig$)).subscribe(response => {
      if (this.selectedIStandardConfigDto != response) {
        this.selectedIStandardConfigDto = response;
      }
      else {
        if (this.selectedIStandardConfigDto !== null && this.selectedIStandardConfigDto !== undefined && this.gridApi) {
          if (this.selectedIStandardConfigDto.StandardConfigurationItemDtos.filter(x => x.StandardConfigurationItemId > 0).length === 0) { this.projectTypeEditable = true }
          else { this.projectTypeEditable = false }
          this.gridApi.refreshCells();
        }
      }
      this.setRowFromItem(response);
    });

  }

  async loadGrid() {
    if (!this.gridApi) return;
    this.stdCfgSharedServiceService.editMode = false;
    if (this.selectedFunction) {
      this.gridApi.setColumnsVisible(["ConstructionTypeId"], this.selectedFunction.FunctionId === FunctionIds.Facilities ? true : false);
      this.iStandardConfigDtos = this.iStandardConfigDtosAll.filter(s => s.FunctionId === this.selectedFunction.FunctionId);
      this.gridApi.setGridOption('rowData', this.iStandardConfigDtos);
    } else {
      this.gridApi.setGridOption('rowData', null);
      this.gridApi.setGridOption('loading', false);
    }
  }

  defaultExcelExportParams = {
    processCellCallback: (params) => {
      if (params.column.userProvidedColDef && params.column.userProvidedColDef.type
        && params.column.userProvidedColDef && params.column.userProvidedColDef.type.length > 0
        && params.column.userProvidedColDef.type.includes('dateColumn')
        && params.value) {
        return params.value ? moment(params.value).format('L') : ''
      }
      return params.value;
    },
  }

  getIProjectTypeDtosById = (id) => {
    if (!id) return '';
    let atype = this.IProjectTypeDtos.find(a => a.ProjectTypeId == id);
    if (atype) return atype.Description;
    return '';
  }

  getIProjectSubTypeDtosById = (id) => {
    if (!id) return '';
    let atype = this.IProjectSubTypeDtos.find(a => a.ProjectSubTypeId == id);
    if (atype) return atype.Description;
    return '';
  }

  getIConstructionTypeDtosById = (id) => {
    if (!id) return '';
    let atype = this.IConstructionTypeDtos.find(a => a.ConstructionTypeId == id);
    if (atype) return atype.Description;
    return '';
  }

  async onCellValueChanged(e: CellValueChangedEvent) {
    try {
      if (e.oldValue == e.newValue) return;

      this.stdCfgSharedServiceService.editMode = true;
      this.stdCfgSharedServiceService.editedEntities.set(e.data.StandardConfigurationId, e.data);
      this.gridApi.applyTransaction({ update: [e.data] });
    }
    catch (e) {
      console.warn('Error on cell value change:', e);
    }
  }

  onCellEditingStarted(e: CellEditingStartedEvent) {
    this.editedRow = e.node;
  }

  async onSelectionChanged(e: SelectionChangedEvent) {
    try {
      let selectedRow = this.gridApi.getSelectedRows()[0];

      if (this.selectedIStandardConfigDto == selectedRow) return;

      if (this.selectedIStandardConfigDto === null || this.selectedIStandardConfigDto === undefined || this.stdCfgSharedServiceService.editedEntities.size === 0) {
        await this.stdCfgSharedServiceService.setSelectedStandardConfigDto(selectedRow);
      }
      else if (this.confirmDoNotChangeStdConfig === false) {
        this.pendingIStandardConfigDto = selectedRow;
      }
      else if (this.confirmDoNotChangeStdConfig === true) {
        this.confirmDoNotChangeStdConfig = false;
      }
    }
    catch (e) {
      console.warn('Error on selection change:', e);
    }
  }

  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    await this.loadGrid();
  }

  public confirmCancel() {
    this.dialogOpened = false;
    this.editedRow.setSelected(true, true);
    this.pendingIStandardConfigDto = null;
    this.confirmDoNotChangeStdConfig = true;
  }

  public async confirmSave() {
    let saved = await this.stdCfgSharedServiceService.saveChanges();
    if (saved) {
      this.editedRow = null;
      await this.stdCfgSharedServiceService.setSelectedStandardConfigDto(this.pendingIStandardConfigDto);
      this.pendingIStandardConfigDto = null;
    } else {
      this.editedRow.setSelected(true, true);
      this.pendingIStandardConfigDto = null;
      this.confirmDoNotChangeStdConfig = true;
    }
    this.dialogOpened = false;
  }

  public async confirmDontSave() {
    this.editedRow = null;
    await this.stdCfgSharedServiceService.setSelectedStandardConfigDto(this.pendingIStandardConfigDto);
    this.pendingIStandardConfigDto = null;
    await this.stdCfgSharedServiceService.cancelNewStandardConfigDto();
    this.dialogOpened = false;
  }
}